import React from 'react';
import {
    Box,
    Card,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { ServiceLog } from '../serviceLog';
import { formatCurrency, formatDate } from '../../../../utils/utils';

interface ServiceLogListProps {
    serviceLogs: ServiceLog[];
    loading: boolean;
    onEdit: (log: ServiceLog) => void;
    onDelete: (logId: string) => void;
    onView: (log: ServiceLog) => void;
}

const tableStyles = {
    '& .MuiTableCell-root': {
        py: 1,
        px: 1,
        fontSize: '0.75rem',
    },
    '& .MuiChip-root': {
        height: 24,
        fontSize: '0.75rem',
    },
    '& .MuiIconButton-root': {
        padding: 1,
    },
};

const ServiceLogList: React.FC<ServiceLogListProps> = ({
    serviceLogs,
    loading,
    onEdit,
    onDelete,
    onView,
}) => {
    if (loading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <Typography variant="body2">Loading...</Typography>
            </Box>
        );
    }

    if (serviceLogs.length === 0) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <Typography variant="body2">No service logs found</Typography>
            </Box>
        );
    }

    return (
        <Card>
            <TableContainer>
                <Table size="small" sx={tableStyles}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Type</TableCell>
                            <TableCell>Garage</TableCell>
                            <TableCell>Odometer</TableCell>
                            <TableCell>Duration Since Last</TableCell>
                            <TableCell>Amount</TableCell>
                            <TableCell>GST</TableCell>
                            <TableCell>Total Amount</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {serviceLogs.map((log) => (
                            <TableRow key={log.id}>
                                <TableCell>
                                    {log.createdAt ? formatDate(log.createdAt) : '-'}
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={log.serviceType}
                                        color={log.serviceType === 'FREE' ? 'success' : 'default'}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell>{log.garageDetails.name}</TableCell>
                                <TableCell>{log.odometer} km</TableCell>
                                <TableCell>
                                    {log.durationSinceLastService ? `${log.durationSinceLastService} months` : '-'}
                                </TableCell>
                                <TableCell>{formatCurrency(log.amount)}</TableCell>
                                <TableCell>{formatCurrency(log.gst)}</TableCell>
                                <TableCell>{formatCurrency(log.totalAmount)}</TableCell>
                                <TableCell align="right">
                                    {/* view button */}
                                    <Tooltip title="View">
                                        <IconButton
                                            size="small"
                                            onClick={() => onView(log)}
                                            color="primary"
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    {/* view button */}
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => onEdit(log)}
                                            color="primary"
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => onDelete(log.id!)}
                                            color="error"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};

export default ServiceLogList; 